import { ArrowsClockwise, BellRinging, Check, Clock, X } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { Nudge } from "~/api/opsTypes";
import { useCancelNudge, useMarkNudgeDone, useNudgeInbox, useSnoozeNudge } from "~/api/queries";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";
import { Switch } from "~/foundation/components/Switch";
import { Text } from "~/foundation/components/Text";
import { formatDateTime } from "~/foundation/utils/time";

/**
 * The nudge lifecycle, given a reader (D5).
 *
 * `nudges.create` shipped in W2 and every one of `list`/`snooze`/`done`/`cancel` had **zero
 * callers** in kms (§0 C5): a created reminder could only ever be dismissed from the
 * notification it produced. Four endpoints existed and the door only opened one way. This is
 * the other side of it.
 */

const SNOOZE_HOURS = [1, 24, 168];

function statusTone(status: Nudge["status"]): string {
  if (status === "fired") {
    return "text-accent";
  }
  if (status === "done" || status === "cancelled") {
    return "text-text-tertiary";
  }
  return "text-text-secondary";
}

function NudgeRow({ nudge, locale }: { nudge: Nudge; locale: string }) {
  const { t } = useTranslation();
  const snooze = useSnoozeNudge();
  const markDone = useMarkNudgeDone();
  const cancel = useCancelNudge();
  const resolved = nudge.status === "done" || nudge.status === "cancelled";
  const busy = snooze.isPending || markDone.isPending || cancel.isPending;

  const snoozeBy = (hours: number) => {
    const when = new Date(Date.now() + hours * 3600_000);
    snooze.mutate(
      // Always an ISO string with a zone: the server parses `AssumeUniversal`, so a naive
      // local-time string would be read as UTC (§E2 R11).
      { id: nudge.id, whenUtc: when.toISOString() },
      {
        onSuccess: () => toast.success(t("Snoozed")),
        onError: (error) =>
          toast.error(error instanceof Error ? error.message : t("Couldn't snooze that")),
      },
    );
  };

  return (
    <li className="flex items-center gap-3 border-b border-divider py-2 last:border-b-0">
      <BellRinging size={16} aria-hidden className="shrink-0 text-text-tertiary" />
      <div className="min-w-0 flex-1">
        <Text variant="body" className="block truncate">
          {nudge.note ?? t("Reminder")}
        </Text>
        <Text variant="small" className={`block ${statusTone(nudge.status)}`}>
          {formatDateTime(nudge.whenUtc, locale)}
          {nudge.recurrenceRule
            ? ` · ${nudge.recurrenceRule.replace("FREQ=", "").toLowerCase()}`
            : ""}
          {` · ${t(nudge.status)}`}
        </Text>
      </div>

      {resolved ? null : (
        <>
          {SNOOZE_HOURS.map((hours) => (
            <Button
              key={hours}
              variant="ghost"
              size="sm"
              icon={Clock}
              disabled={busy}
              onClick={() => snoozeBy(hours)}
            >
              {hours === 1 ? t("1h") : hours === 24 ? t("1d") : t("1w")}
            </Button>
          ))}
          <Button
            variant="ghost"
            size="sm"
            icon={Check}
            disabled={busy}
            onClick={() =>
              markDone.mutate(nudge.id, { onSuccess: () => toast.success(t("Marked done")) })
            }
          >
            {t("Done")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            icon={X}
            disabled={busy}
            onClick={() =>
              cancel.mutate(nudge.id, { onSuccess: () => toast.success(t("Reminder cancelled")) })
            }
          >
            {t("Cancel")}
          </Button>
        </>
      )}
    </li>
  );
}

export function Reminders() {
  const { t, i18n } = useTranslation();
  const [includeResolved, setIncludeResolved] = useState(false);
  const { data: nudges, isLoading, isError, refetch, isFetching } = useNudgeInbox(includeResolved);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <Text variant="secondary">
            {/* ⚠ `nudges.list` is creator-scoped ON TOP of the team filter — an admin cannot see
                anyone else's. An empty list here is never evidence the workspace has none. */}
            {t(
              "Only your own reminders appear here — nobody else can see them, and you cannot see theirs.",
            )}
          </Text>
        </div>
        <Switch
          checked={includeResolved}
          onCheckedChange={setIncludeResolved}
          label={t("Show completed and cancelled")}
        />
        <Button
          variant="ghost"
          size="sm"
          icon={ArrowsClockwise}
          disabled={isFetching}
          onClick={() => void refetch()}
        >
          {t("Refresh")}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner label={t("Loading")} />
        </div>
      ) : isError ? (
        <Empty message={t("Your reminders could not be loaded")} />
      ) : (nudges ?? []).length === 0 ? (
        <Empty message={t("You haven't asked to be reminded about anything yet.")} />
      ) : (
        <ul className="flex flex-col">
          {(nudges ?? []).map((nudge) => (
            <NudgeRow key={nudge.id} nudge={nudge} locale={i18n.language} />
          ))}
        </ul>
      )}
    </section>
  );
}
